import React, { useMemo } from 'react';
import { Field, useFormState } from 'react-final-form';
import { Alert, Checklist, DatePickerWithInput, NumberInput, RadioGroup, SelectComponent, Switch, TimePickerWithInput } from '@utriper/core-ui';
import { Container, Label } from '../../styles';
import { Box, FormHelperText, Grid2, Typography } from '@mui/material';
import { buildRRule, FREQUENCY, UNTIL_TYPE } from '../../utils';
import { useTranslation } from 'react-i18next';

const RepeatTab = ({ form, isMobile }) => {
    const { t, i18n } = useTranslation();
    const translate = (key) => t(`experience_calendar.schedule_form.repeat_tab.${key}`);
    const { values, errors, submitFailed } = useFormState();
    const frequencyOptions = [
        {
            id: FREQUENCY.DAILY,
            name: translate('frequency_options.daily'),
        },
        {
            id: FREQUENCY.WEEKLY,
            name: translate('frequency_options.weekly'),
        },
        {
            id: FREQUENCY.WEEKENDS,
            name: translate('frequency_options.weekends'),
        },
        {
            id: FREQUENCY.BUSINESS_DAYS,
            name: translate('frequency_options.business_days'),
        },
        {
            id: FREQUENCY.CUSTOM,
            name: translate('frequency_options.custom'),
        },
    ];

    const weekDaysOptions = useMemo(
        () => [
            {
                id: 'MO',
                label: isMobile ? t('copyScheduleModal.mon') : t('copyScheduleModal.monday'),
            },
            {
                id: 'TU',
                label: isMobile ? t('copyScheduleModal.tue') : t('copyScheduleModal.tuesday'),
            },
            {
                id: 'WE',
                label: isMobile ? t('copyScheduleModal.wed') : t('copyScheduleModal.wednesday'),
            },
            {
                id: 'TH',
                label: isMobile ? t('copyScheduleModal.thu') : t('copyScheduleModal.thursday'),
            },
            {
                id: 'FR',
                label: isMobile ? t('copyScheduleModal.fri') : t('copyScheduleModal.friday'),
            },
            {
                id: 'SA',
                label: isMobile ? t('copyScheduleModal.sat') : t('copyScheduleModal.saturday'),
            },
            {
                id: 'SU',
                label: isMobile ? t('copyScheduleModal.sun') : t('copyScheduleModal.sunday'),
            },
        ],
        [isMobile, t]
    );

    const untilOptions = [
        {
            id: UNTIL_TYPE.INFINITE,
            label: translate('until_options.infinite_label'),
        },
        {
            id: UNTIL_TYPE.DATE,
            label: (
                <Grid2 container size={12} gap={1} flexWrap={'nowrap'}>
                    <Grid2 container size={3} alignItems="center">
                        <Typography>{translate('until_options.until_date_label')}</Typography>
                    </Grid2>
                    <Grid2 size={9}>
                        <Field
                            id="untilDate"
                            name="untilDate"
                            render={({ input }) => (
                                <DatePickerWithInput
                                    {...input}
                                    placeholder={translate('until_options.until_date_placeholder')}
                                    style={{
                                        width: '100%',
                                    }}
                                    disabled={values?.untilType !== UNTIL_TYPE.DATE}
                                    useUTC
                                />
                            )}
                            value={values?.untilDate}
                            onChange={(date) => {
                                if (!Date.parse(date) || Date.now() > date) return;

                                form.change('untilDate', date);
                            }}
                        />
                    </Grid2>
                </Grid2>
            ),
        },
        {
            id: UNTIL_TYPE.COUNT,
            label: (
                <Grid2 container size={12} gap={1} flexWrap={'nowrap'}>
                    <Grid2 container size={3} alignItems="center">
                        <Typography>{translate('until_options.until_count_title')}</Typography>
                    </Grid2>
                    <Grid2 size={9}>
                        <Field id="untilCount" name="untilCount" component={NumberInput} label={translate('until_options.until_count_label')} placeholder={translate('until_options.until_count_placeholder')} disabled={values?.untilType !== UNTIL_TYPE.COUNT} />
                    </Grid2>
                </Grid2>
            ),
        },
    ];

    const rrule = useMemo(() => {
        if (values?.repeat && values?.frequency && values?.start && values?.startDuration && values?.endDuration) {
            if (values?.frequency === FREQUENCY.CUSTOM && values?.selectedWeekDays?.length <= 0) {
                return null;
            }

            const translateOptions = { translate: (key, options) => t(`experience_calendar.schedule_form.rrule_description.${key}`, options), language: i18n.language };
            return buildRRule(values, translateOptions);
        }

        return null;
    }, [values, i18n.language, t]);

    return (
        <React.Fragment>
            {/* Repeat Switch */}
            <Container>
                <Field id="repeat" name="repeat" type="checkbox" component={Switch} label={translate('repeat_label')} />
            </Container>

            {values?.repeat && (
                <React.Fragment>
                    {/* Frequency */}
                    <Container>
                        <Label>{translate('frequency_label')}</Label>
                        <Field id="frequency" name="frequency" component={SelectComponent} label={translate('frequency_placeholder')} values={frequencyOptions} hasError={submitFailed && errors?.frequency} errorMessage={submitFailed && errors?.frequency ? errors.frequency : null} />
                    </Container>

                    {values?.frequency === FREQUENCY.CUSTOM && (
                        <Container>
                            <Label>{translate('custom_frequency_label')}</Label>
                            <Field id="selectedWeekDays" name="selectedWeekDays" component={Checklist} orientation="horizontal" options={weekDaysOptions} />
                        </Container>
                    )}

                    {/* Intraday Repeat Switch */}
                    <Container>
                        <Field id="intradayRepeat" name="intradayRepeat" type="checkbox" component={Switch} label={translate('intraday_label')} />
                    </Container>

                    {/* Intraday Repeat hours and minutes */}
                    {values?.intradayRepeat && (
                        <React.Fragment>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    '& .MuiFormControl-root': {
                                        maxWidth: '96px !important',
                                    },
                                }}
                            >
                                <Typography fontSize={16} fontWeight={500}>
                                    {translate('intraday_repeat')}
                                </Typography>
                                <Field
                                    id="byHourDate"
                                    name="byHourDate"
                                    component={TimePickerWithInput}
                                    views={['hours']}
                                    value={values?.byHourDate}
                                    onChange={(newDate) => {
                                        if (!newDate || !newDate.isValid) return;

                                        form.change('byHourDate', newDate);
                                        form.change('byHour', newDate.hour);
                                    }}
                                    useUTC
                                />
                                <Typography fontSize={16} fontWeight={500}>
                                    {translate('by_hour_label')}
                                </Typography>
                                <Field
                                    id="byMinuteDate"
                                    name="byMinuteDate"
                                    component={TimePickerWithInput}
                                    views={['minutes']}
                                    value={values?.byMinuteDate}
                                    onChange={(newDate) => {
                                        if (!newDate || !newDate.isValid) return;

                                        form.change('byMinuteDate', newDate);
                                        form.change('byMinute', newDate.minute);
                                    }}
                                    useUTC
                                />
                                <Typography fontSize={16} fontWeight={500}>
                                    {translate('by_minutes_label')}
                                </Typography>
                            </Box>
                        </React.Fragment>
                    )}

                    {/* UNTIL RRULE */}
                    <Container
                        sx={{
                            '& .MuiFormControlLabel-label': {
                                width: '100%',
                            },
                        }}
                    >
                        <Label>{translate('until_label')}</Label>
                        <Field id="untilType" name="untilType" component={RadioGroup} options={untilOptions} />
                        <FormHelperText>{translate('until_helper')}</FormHelperText>
                    </Container>

                    {rrule && <Alert title={rrule?.rruleDescription} severity="info" />}
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default RepeatTab;
